import type { FieldRule } from "@productlane/lib"

import type { SchemaField } from "./tool-schema-fields"
import {
	castForField,
	defaultKindFor,
	emptyRuleFor,
	isContainerKind,
} from "./typed-value"

export type Mode = FieldRule["mode"]

export const MODE_LABELS: Record<Mode, string> = {
	any: "Let AI decide",
	blocked: "Never sent",
	fixed: "Fixed value",
	json: "Fixed value",
	ref: "From secure data",
	enum: "Allowed values",
	template: "Template",
}

export const MODE_HINTS: Record<Mode, string> = {
	any: "The agent writes it.",
	blocked: "Never passed on.",
	fixed: "Always your value.",
	json: "Always your value.",
	ref: "From widget or connector data.",
	enum: "Picked from your list.",
	template: "Your text with refs.",
}

export const MODE_ORDER: Mode[] = [
	"any",
	"ref",
	"template",
	"fixed",
	"enum",
	"blocked",
]

/**
 * The modes a field can take, read off its own schema. A pinned value is always
 * offered as `fixed`; defaultRuleFor turns it into `json` for a container.
 */
export function modesFor(field: SchemaField): Mode[] {
	// Nothing is declared, so every mode stays reachable, as it was before types.
	if (field.kinds.length === 0) return MODE_ORDER

	const takesText = field.kinds.some((kind) => !isContainerKind(kind))

	return MODE_ORDER.filter((mode) => {
		// An enum value is a scalar, so an enum rule on a container never matches.
		// A reference and a template stay available: a json cast parses their result.
		if (mode === "enum") return takesText
		return true
	})
}

/** A stored rule the field's own schema can no longer satisfy. */
export function unsatisfiableReason(
	field: SchemaField,
	rule: FieldRule,
): string | null {
	if (field.kinds.length === 0) return null

	if (rule.mode === "enum" && field.kinds.every(isContainerKind)) {
		return `This argument takes ${field.kinds.join(" or ")}, and an allowed-values list only holds text and numbers.`
	}
	return null
}

export function defaultRuleFor(mode: Mode, field?: SchemaField): FieldRule {
	const kind = field ? defaultKindFor(field) : "string"
	const cast = field ? castForField(field) : undefined

	switch (mode) {
		case "fixed":
		case "json":
			return emptyRuleFor(kind, field)
		case "ref":
			return { mode: "ref", ref: "", ...(cast && { cast }) }
		case "enum":
			return { mode: "enum", values: field?.enumValues ?? [] }
		case "template":
			return { mode: "template", template: "", ...(cast && { cast }) }
		case "blocked":
			return { mode: "blocked" }
		case "any":
			return { mode: "any" }
	}
}

// ref and template read the same chain, so switching between them keeps it.
export function carryChain(current: FieldRule, next: FieldRule): FieldRule {
	const chained = current.mode === "ref" || current.mode === "template"
	const takes = next.mode === "ref" || next.mode === "template"
	if (!chained || !takes || !current.fallbacks?.length) return next
	return { ...next, fallbacks: current.fallbacks }
}
